import { allPeriodsCountYearAndMonth, defaultMonthsRangeSettings } from "@package/grid-utils";
import dayjs from "dayjs";

export type ChartablePeriod = { label: string; value: string };

// Extra units beyond the exact month horizon, so the last months are never on the edge of an aggregation's range.
const aggregationBuffer = 4;

// Every calendar aggregation must span at least the month horizon. If one is shorter, the periods past its range have no sort index and get dumped out of position. Derive each count from the month range so bumping the horizon can never leave one short again.
export const buildChartablePeriodOrder = (date: Date): string[] => {
  const months = defaultMonthsRangeSettings;
  const count = (monthsPerUnit: number) => Math.ceil(months / monthsPerUnit) + aggregationBuffer;
  const periods = allPeriodsCountYearAndMonth(
    {
      months,
      qtrs: count(3),
      qtr_current: true,
      hlvs: count(6),
      hlv_current: true,
      cals: count(12),
      cal_current: true,
      seasons: count(6),
      season_current: true,
    },
    date,
  );
  return [...periods.months, ...periods.qtrs, ...periods.hlvs, ...periods.cals, ...periods.seasons].map(([_, p]) => p);
};

export const buildPeriodRank = (order: string[]) => {
  const orderIndex = new Map<string, number>(order.map((label, index) => [label, index]));
  // Anything not in the order sorts to the bottom rather than the top, so a stray out-of-range period never displaces the months.
  return (label: string) => orderIndex.get(label) ?? Number.MAX_SAFE_INTEGER;
};

const isMonthRegex = /\d{4}-\d{2}-\d{2}/;

export const parseChartablePeriod = (period: string): ChartablePeriod => {
  if (isMonthRegex.test(period)) {
    return { label: dayjs(period).format("MMMYY").toUpperCase(), value: period.substring(0, 10) };
  }

  // Calendar aggregation, e.g. "Q126" -> "Q1 26"
  const name = period.slice(0, -2);
  const value = period.slice(-2);
  return { label: `${name} ${value}`, value: period };
};

const defaultRank = buildPeriodRank(buildChartablePeriodOrder(new Date()));

export const sortChartablePeriods = (
  periods: string[],
  rank: (label: string) => number = defaultRank,
): ChartablePeriod[] => periods.map(parseChartablePeriod).sort((a, b) => rank(a.label) - rank(b.label));
